import Phaser from 'phaser';
import { CELESTIAL_PURPLE } from '../skills/celestial-active';
import { HUD_TEXT } from '../fx/readability';
import { isTouchDevice } from '../../shared/input-device';

/**
 * 戰力滿提示文案。手機/平板沒有空白鍵 → 指向右下角 ⚡ 鈕(touch-controls 的
 * 唯一大絕施放入口);桌機才講「空白」。boss 2026-06-13「上面好像都弄不到」修。
 */
export const POWER_READY_HINT_TOUCH = '⚡ 戰力滿!點右下 ⚡ 釋放';
export const POWER_READY_HINT_KEYBOARD = '⚡ 戰力滿!按 空白 釋放';

/**
 * v1.1 星算門 active-skill HUD — 4 cooldown slots (bottom) + 3-cell power gauge
 * (top). Pure presentation: the scene drives state via `update*()` each frame.
 *
 * All Phaser calls are try-guarded so the headless test harness (no add.*,
 * no tweens) constructs + updates without throwing.
 *
 * 設計給小孩看得懂:每格大圖示 + 條狀冷卻 + 剩餘秒數;CD 中整格變灰。
 */

const SLOT_SIZE = 52;
const SLOT_GAP = 10;
const SLOT_MARGIN_BOTTOM = 14;

/** Per-slot config the scene passes in at construction time. */
export interface SkillSlotConfig {
  /** Emoji / glyph shown on the slot. */
  icon: string;
  /** Short label under the icon (e.g. '連發'). */
  label: string;
  /** When false the slot renders as a locked grey 「未解鎖」 cell. */
  unlocked: boolean;
}

interface SlotGfx {
  bg: Phaser.GameObjects.Rectangle | null;
  fill: Phaser.GameObjects.Rectangle | null; // cooldown sweep (bottom-up)
  icon: Phaser.GameObjects.Text | null;
  label: Phaser.GameObjects.Text | null;
  cdText: Phaser.GameObjects.Text | null;
  config: SkillSlotConfig;
}

export class SkillHud {
  private slots: SlotGfx[] = [];
  private powerCells: Phaser.GameObjects.Rectangle[] = [];
  private powerLabel: Phaser.GameObjects.Text | null = null;
  private readonly width: number;
  private readonly height: number;

  constructor(
    private scene: Phaser.Scene,
    configs: SkillSlotConfig[],
    gameWidth: number,
    gameHeight: number,
  ) {
    this.width = gameWidth;
    this.height = gameHeight;
    this._buildSlots(configs);
    this._buildPowerGauge();
  }

  private _buildSlots(configs: SkillSlotConfig[]): void {
    const n = configs.length;
    const totalW = n * SLOT_SIZE + (n - 1) * SLOT_GAP;
    const startX = (this.width - totalW) / 2 + SLOT_SIZE / 2;
    const cy = this.height - SLOT_MARGIN_BOTTOM - SLOT_SIZE / 2;

    for (let i = 0; i < n; i++) {
      const cx = startX + i * (SLOT_SIZE + SLOT_GAP);
      const cfg = configs[i];
      const g: SlotGfx = {
        bg: null, fill: null, icon: null, label: null, cdText: null, config: cfg,
      };
      try {
        g.bg = this.scene.add.rectangle(cx, cy, SLOT_SIZE, SLOT_SIZE,
          cfg.unlocked ? 0x1a1030 : 0x222222, 0.85)
          .setStrokeStyle(2, cfg.unlocked ? CELESTIAL_PURPLE : 0x555555)
          .setDepth(40);
        // cooldown sweep fill — anchored at bottom, grows to cover slot while on CD.
        g.fill = this.scene.add.rectangle(cx, cy + SLOT_SIZE / 2, SLOT_SIZE - 4, 0, 0x000000, 0.55)
          .setOrigin(0.5, 1)
          .setDepth(41);
        g.icon = this.scene.add.text(cx, cy - 6, cfg.unlocked ? cfg.icon : '🔒', {
          fontSize: '22px', fontFamily: 'sans-serif',
        }).setOrigin(0.5).setDepth(42);
        g.label = this.scene.add.text(cx, cy + 16, cfg.unlocked ? cfg.label : '未解鎖', {
          fontSize: '10px', fontFamily: 'sans-serif',
          color: cfg.unlocked ? '#d1b3ff' : '#777777',
        }).setOrigin(0.5).setDepth(42);
        // key number badge (top-left of slot)
        this.scene.add.text(cx - SLOT_SIZE / 2 + 8, cy - SLOT_SIZE / 2 + 8, String(i + 1), {
          fontSize: '11px', fontFamily: 'sans-serif', color: '#ffd54f',
        }).setOrigin(0.5).setDepth(43);
        g.cdText = this.scene.add.text(cx, cy, '', {
          fontSize: '16px', fontFamily: 'sans-serif', color: '#ffffff',
          fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(44);
      } catch { /* headless */ }
      this.slots.push(g);
    }
  }

  private _buildPowerGauge(): void {
    const cellW = 56;
    const cellH = 14;
    const gap = 6;
    const n = 3;
    const totalW = n * cellW + (n - 1) * gap;
    const startX = (this.width - totalW) / 2 + cellW / 2;
    // W1 戰場可讀性:label 12px 手機縮放後 ~6px 看不見 → HUD_TEXT.fontPxPowerLabel
    // + 深色描邊;格子整體下移讓加大的 label 不被畫布頂裁切。
    const cy = 38;
    try {
      this.powerLabel = this.scene.add.text(startX - cellW / 2, cy - 26, '⚡ 戰力槽', {
        fontSize: HUD_TEXT.fontPxPowerLabel + 'px', fontFamily: 'sans-serif', color: '#ffd54f',
        fontStyle: 'bold', stroke: HUD_TEXT.strokeColor, strokeThickness: HUD_TEXT.strokePx,
      }).setOrigin(0, 0.5).setDepth(40);
      for (let i = 0; i < n; i++) {
        const cx = startX + i * (cellW + gap);
        // empty cell frame
        this.scene.add.rectangle(cx, cy, cellW, cellH, 0x111111, 0.7)
          .setStrokeStyle(1, 0x6a4a9a).setDepth(40);
        const cell = this.scene.add.rectangle(cx, cy, cellW - 2, cellH - 2, CELESTIAL_PURPLE, 1)
          .setDepth(41).setVisible(false);
        this.powerCells.push(cell);
      }
    } catch { /* headless */ }
  }

  /**
   * Update cooldown sweep + remaining-seconds text per slot.
   * @param fractions per-slot cooldown progress 0..1 (1 = ready).
   * @param remainingMs per-slot ms left (0 = ready).
   */
  updateCooldowns(fractions: number[], remainingMs: number[]): void {
    for (let i = 0; i < this.slots.length; i++) {
      const g = this.slots[i];
      if (!g.config.unlocked) continue;
      const frac = fractions[i] ?? 1;
      const remMs = remainingMs[i] ?? 0;
      const onCd = frac < 1;
      try {
        // dark overlay covers the still-cooling portion (bottom-up shrink).
        if (g.fill) {
          const coveredH = (SLOT_SIZE - 4) * (1 - frac);
          g.fill.setSize(SLOT_SIZE - 4, coveredH);
          g.fill.setVisible(onCd);
        }
        if (g.icon) g.icon.setAlpha(onCd ? 0.4 : 1);
        if (g.label) g.label.setAlpha(onCd ? 0.4 : 1);
        if (g.cdText) {
          if (onCd) {
            g.cdText.setText(Math.ceil(remMs / 1000).toString());
            g.cdText.setVisible(true);
          } else {
            g.cdText.setVisible(false);
          }
        }
        if (g.bg) g.bg.setFillStyle(onCd ? 0x101018 : 0x1a1030, 0.85);
      } catch { /* headless */ }
    }
  }

  /**
   * Update the 3-cell power gauge. @param fraction 0..1 ultimate charge.
   * Each cell lights when its 33% threshold is crossed; full → all 3 + gold.
   */
  updatePower(fraction: number): void {
    const f = Math.min(1, Math.max(0, fraction));
    for (let i = 0; i < this.powerCells.length; i++) {
      const threshold = (i + 1) / this.powerCells.length;
      const lit = f >= threshold - 1e-6;
      try {
        this.powerCells[i].setVisible(lit);
        this.powerCells[i].setFillStyle(f >= 1 ? 0xffd54f : CELESTIAL_PURPLE, 1);
      } catch { /* headless */ }
    }
    try {
      if (this.powerLabel) {
        // While the deny flash is showing, don't clobber its「戰力未滿」text.
        if (this._powerDenyUntilMs > Date.now()) { /* leave deny text */ }
        else if (f >= 1) { this.powerLabel.setText(isTouchDevice() ? POWER_READY_HINT_TOUCH : POWER_READY_HINT_KEYBOARD); this.powerLabel.setColor('#ffd54f'); }
        else { this.powerLabel.setText('⚡ 戰力槽'); this.powerLabel.setColor('#ffd54f'); }
      }
    } catch { /* headless */ }
  }

  /**
   * Juice Batch 1 — 技能槽「剛變 ready」脈衝。在槽位畫一圈短暫亮環(門派紫)
   * scale 1→1.5 + alpha 1→0,~360ms 後 destroy,給「可用了!」的可見兌現。
   * 純呈現、全 try-guarded(headless 直接 no-op)。
   *
   * @param slotIndex 槽位 index(0-3)。越界 / 鎖住 / headless 皆 no-op。
   */
  pulseSlotReady(slotIndex: number): void {
    const g = this.slots[slotIndex];
    if (!g || !g.config.unlocked || !g.bg) return;
    try {
      const cx = g.bg.x;
      const cy = g.bg.y;
      const ring = this.scene.add.rectangle(cx, cy, SLOT_SIZE + 6, SLOT_SIZE + 6, 0x000000, 0)
        .setStrokeStyle(3, 0xffd54f, 1)
        .setDepth(45);
      this.scene.tweens?.add?.({
        targets: ring,
        scaleX: 1.5, scaleY: 1.5,
        alpha: 0,
        duration: 360,
        ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  /** When this wall-clock ms is in the future, updatePower keeps the deny text. */
  private _powerDenyUntilMs = 0;

  /**
   * BUG FIX (boss「空格按不了」): brief「戰力未滿」flash on the power label when
   * the player taps the ultimate before the gauge is full. Reverts to the
   * normal label after ~700ms via the _powerDenyUntilMs guard in updatePower.
   */
  flashPowerNotReady(): void {
    this._powerDenyUntilMs = Date.now() + 700;
    try {
      if (this.powerLabel) {
        this.powerLabel.setText('⚡ 戰力未滿');
        this.powerLabel.setColor('#ff8a80');
        // scale punch for a noticeable nudge.
        this.scene.tweens?.add?.({
          targets: this.powerLabel,
          scale: { from: 1.25, to: 1 },
          duration: 250,
          ease: 'Cubic.easeOut',
        });
      }
    } catch { /* headless */ }
  }
}
